import useHasAccessForTierDeterminator from 'dashboard/apps/InquiryTemplateEditor/hooks/useHasAccessForTierDeterminator';
import useHasITEWritePermissionsForObjects from 'dashboard/apps/InquiryTemplateEditor/hooks/useHasITEWritePermissionsForObjects';
import useIsEditableVersion from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableVersion';
import useIsITEInternal from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsITEInternal';
import { ProductFeatureName } from 'dashboard/models/organization';

// previously contained both verification-template and workflow
// keeping this type for now in case RelatedObjects are expanded,
// but the goal is to eliminate related object perms checking entirely.
export type RelatedObjectType = 'verification-template';

export enum EditorAccessTier {
  Any = 'any',
  InquiriesFree = ProductFeatureName.InquiriesFree,
  InquiriesBasic = ProductFeatureName.InquiriesBasic,
  InquiriesEnhanced = ProductFeatureName.InquiriesEnhanced,
}

export type EditableOptions = {
  accessTier?: EditorAccessTier;
  accessTierInternal?: EditorAccessTier;
  requiredWritePermissions?: RelatedObjectType[];
  internalOnly?: boolean;
};

/**
 * @name `useIsEditableWithOptions`
 *
 * A hook that returns whether the component/section of Flow Editor in
 * which it's used is editable, given the options it's passed.
 *
 * The current options are:
 * - accessTier: the product feature tier, or higher, that is required for access by external users.
 *   e.g InquiriesBasic means that both basic + enhanced should have edit access. Defaults to 'any',
 *   meaning any product feature tier (including having no product feature) should have access
 * - accessTierInternal: same as `accessTier` but applied specifically to internal users. Defaults to the
 *   same value as `accessTier` unless specified
 * - internalOnly: gates edit access to @withpersona or impersonating users. this differs from
 *   isITEInternal because it also combines isEditableVersion to display the expected immutable
 *   behavior when viewing old published versions, discarded drafts, etc.
 */
export default (options: EditableOptions = {}) => {
  const {
    accessTier = EditorAccessTier.Any,
    accessTierInternal,
    requiredWritePermissions = [],
    internalOnly,
  } = options;
  const isITEInternal = useIsITEInternal();
  // If internal and `accessTierInternal` not specified, default to `accessTier`
  const effectiveAccessTier = isITEInternal ? accessTierInternal || accessTier : accessTier;

  const hasAccessForEffectiveTier = useHasAccessForTierDeterminator()(effectiveAccessTier);
  const isEditableVersion = useIsEditableVersion();
  const hasWritePermissions = useHasITEWritePermissionsForObjects(requiredWritePermissions);

  if (!isEditableVersion) {
    return false;
  }

  // if internal only, only block editing for immutable versions -
  // currently includes things like embedded workflow editor and fields panel
  if (internalOnly) {
    return isITEInternal;
  }

  // Block editing for external users without write permissions - this access level includes theme editor,
  // copy editing, template name + description, etc.
  return hasAccessForEffectiveTier && hasWritePermissions;
};
